import { Theme } from '@mui/material';
import { ConditionInput, ConditionOperator, SortingInput } from 'src/graphql/types';
import { SupportedActionQueries } from 'src/pages/Settings/rules/use-rules-engine';
import { CustomViewRaw } from 'src/types';
import { CustomViewEntity } from './custom-view-config/custom-view-config';

export enum CustomViewFormFields {
  Name = 'name',
  Description = 'description',
  Conditions = 'conditions',
  IsPrivate = 'isPrivate',
  Columns = 'columns',
  Icon = 'icon',
  Sort = 'sort',
}

export enum UIType {
  Text = 'text',
  Textarea = 'textarea',
  List = 'list',
  Number = 'number',
  BubbleNumber = 'bubble_number',
  Date = 'date',
  SinceTime = 'since_time',
  Boolean = 'boolean',
  Icon = 'icon',
  Avatar = 'avatar',
  Chip = 'chip',
  // TODO: it should be refactored in scope of 1V-3435 task
  Chips = 'chips',
  // custom UI type is used for displaying attributes configuration, it is not a real type,
  // it highlights that there is no general component mapper available for the field
  // and it should be handled via specific entity based mapper
  Custom = 'custom',
}

export interface CustomViewAttribute<T = string> {
  group: string;
  attribute: T;
  accessor: string;
  attributeLabel: string;
  uiType: UIType;
  options?: { label: string; value: string; id: string }[];
  default?: boolean;
  order?: number;
  dateFormat?: string;
  includeTime?: boolean;
  // getTextColor accepts unknown value as it is gradually changed to be more flexible in future (in scope of 1V-3236)
  // It is done to preserve existing logic which still expects string but at the same time it should expect to handle
  // values as entity or array of objects.
  getTextColor?: (v: unknown, theme: Theme) => string;
  getBackgroundColor?: (v: unknown, theme: Theme) => string;
  getSubTitleText?: (v: string) => string;
  formatValue?: (v: string) => string;
  hideHeader?: boolean;
  sortingKey?: CustomViewSorting['accessor'];
  sortingAggFunction?: CustomViewSorting['aggFunction'];
  // custom attribute default the db layer coalesces unset values to, so sort matches the cell;
  // forwarded to the sort SortingInput.
  sortingCoalesceDefault?: CustomViewSorting['coalesceDefault'];
  // renderValue can be used in cases where fetched data for the column returns an object or array instead of string
  // and this function maps fetched entity to string that should be supplied as input to Cell.
  renderValue?: (v: unknown) => string;
  aggFunction?: SortingInput['aggFunction'];
}

export interface CustomViewSortConfigItem {
  sort?: string | string[]; // gql sorting key
  filter: string; // gql filtering key
  display: string; // front-end path to data
  aggFunction?: SortingInput['aggFunction'];
}

export interface CustomViewSorting<T = string> {
  attribute: T;
  accessor: CustomViewSortConfigItem['sort'];
  label: string;
  desc: boolean;
  default?: boolean;
  id: string;
  value: string;
  aggFunction?: SortingInput['aggFunction'];
  coalesceDefault?: SortingInput['coalesceDefault'];
}

export interface CustomViewSelectedAttribute {
  attribute: string;
  order: number;
}

export type CustomViewFormConditionCustomData = Pick<
  ConditionInput,
  'operator' | 'value' | 'logicalOperator'
>;

export interface CustomViewFormConditionField {
  value: string; // attribute
  accessor: string; // real gql path for the attribute
  label: string;
  pluralLabel?: string;
  uiType: UIType;
  group: string;
  options: ({ id: string; label: string; value: string } & Record<string, unknown>)[];
  fieldOptions?: ({ id: string; label: string; value: string } & Record<string, unknown>)[];
  optionsQuery?: SupportedActionQueries;
  rootEntity: CustomViewEntity;
  // custom attribute default the db layer coalesces unset values to, so a filter matches the cell;
  // forwarded to the ConditionInput.
  coalesceDefault?: string;
}

export interface CustomViewFormConditionItem {
  id?: string;
  value?:
    | string
    | boolean
    | { id: string; label: string; value: string }[]
    | { value: string };
  operator?: ConditionOperator;
  field?: CustomViewFormConditionField;
}

export type CustomView = Omit<
  CustomViewRaw,
  'columns' | 'conditions' | 'sort' | 'entityName'
> & {
  // raw conditions stored in the DB
  conditions: ConditionInput[];
  // gql friendly formatted conditions, the difference between raw values is that attributes already replaced
  // with corrected paths instead of abstract attribute identifiers which we store in the db
  apiConditions: ConditionInput[];
  // form friendly formatted conditions
  formConditions: {
    rules: CustomViewFormConditionItem[];
    combinator: string;
    not: boolean;
  };

  columns: CustomViewAttribute[];
  sort?: CustomViewSorting | null;
  isFavorite: boolean;
  sortOrder: number;
  entityName: CustomViewEntity;
};

export interface CustomViewFormModel {
  [CustomViewFormFields.Icon]: string;
  [CustomViewFormFields.Name]: string;
  [CustomViewFormFields.Description]: string;
  [CustomViewFormFields.IsPrivate]: boolean;
  [CustomViewFormFields.Conditions]: {
    rules: CustomViewFormConditionItem[];
    combinator: string;
    not: boolean;
  };
  // while we store in the db CustomViewSelectedAttribute, we still want to operate with full objects on form level
  // it simplifies the logic, to support that we need to map values before passing to the form with full objects
  // and still save just CustomViewSelectedAttribute interface to the database
  [CustomViewFormFields.Columns]: CustomViewAttribute[];
}
